<div class="tax-v2">
    <h2 class="w-title card-title">Taxes</h2>
    <p class="page-sub">Set up how tax is collected on orders for this business.</p>

    <ng-container *ngIf="isLoadingTaxSettings">
        <div class="conn-card">
            <div class="card-row">
                <div class="tax-skel-head-text">
                    <p-skeleton width="8rem" height="1rem" styleClass="mb-2"></p-skeleton>
                    <p-skeleton width="22rem" height="0.75rem"></p-skeleton>
                </div>
                <p-skeleton width="3rem" height="1.5rem"></p-skeleton>
            </div>
        </div>
        <div class="conn-card">
            <p-skeleton width="10rem" height="1rem" styleClass="mb-2"></p-skeleton>
            <p-skeleton width="26rem" height="0.75rem" styleClass="mb-3"></p-skeleton>
            <div class="grid">
                <div class="col-6"><p-skeleton height="3.2rem"></p-skeleton></div>
                <div class="col-6"><p-skeleton height="3.2rem"></p-skeleton></div>
            </div>
        </div>
    </ng-container>

    <ng-container *ngIf="!isLoadingTaxSettings">

    <div class="conn-card">
        <div class="card-row">
            <div>
                <p class="label">Collect tax</p>
                <p class="desc">Turn off to stop charging tax on every order, regardless of rates configured below.</p>
            </div>
            <p-inputSwitch [ngModel]="collectTax" (onChange)="onCollectTaxToggleRequested($event)"></p-inputSwitch>
        </div>
    </div>

    <!-- TODO re-enable tax disclaimer: hidden via TAX_DISCLAIMER_ENABLED, code kept intact -->
    <div class="disclaimer" *ngIf="collectTax && disclaimerEnabled">
        <strong>Before you continue</strong>
        Default tax rates are provided for reference purposes only. Merchants are responsible for verifying the
        accuracy and applicability of all tax rates before collecting taxes from customers.
        <div class="check-row">
            <p-checkbox
                [binary]="true"
                [ngModel]="disclaimerAcknowledged"
                (ngModelChange)="onDisclaimerAcknowledgedChange($event)"
            ></p-checkbox>
            I acknowledge and accept this disclaimer
        </div>
    </div>

    <div class="conn-card" *ngIf="collectTax">
        <p class="label">Tax calculation method</p>
        <p class="desc" style="margin-bottom: 14px">This determines which address is used to calculate the applicable rate.</p>

        <div class="basis-options">
            <div class="basis-option" [class.selected]="taxBasis === 'shipping'" (click)="taxBasis = 'shipping'">
                <div class="radio" [class.selected]="taxBasis === 'shipping'"></div>
                <div>
                    <p class="basis-title">Customer's shipping address</p>
                    <p class="basis-desc">Standard for ecommerce orders</p>
                </div>
            </div>
            <div class="basis-option" [class.selected]="taxBasis === 'origin'" (click)="taxBasis = 'origin'">
                <div class="radio" [class.selected]="taxBasis === 'origin'"></div>
                <div>
                    <p class="basis-title">Business location</p>
                    <p class="basis-desc">For in-person sales or origin-based states</p>
                </div>
            </div>
        </div>

        <div class="basis-warning" *ngIf="taxBasisWarning">
            {{ taxBasisWarning }}
        </div>
    </div>

    <ng-container *ngIf="collectTax">
        <div class="conn-card">
            <div class="card-row" style="align-items: flex-start">
                <div>
                    <p class="label" style="margin: 0">{{ taxBasis === 'origin' ? 'Your business locations' : 'States you collect tax in' }}</p>
                    <p class="desc" style="margin-top: 4px;" [ngStyle]="{ 'max-width': taxBasis === 'origin' ? 'none' : '560px', 'white-space': taxBasis === 'origin' ? 'nowrap' : 'normal' }">
                        {{ taxBasis === 'origin'
                            ? 'Origin-based tax uses your business\'s own rate for every order, regardless of where it ships.'
                            : 'Only add a state if you\'re required to collect tax there. Adding a state turns tax collection on for it.' }}
                    </p>
                </div>
                <button pButton pRipple type="button" label="+ Add a tax" class="p-button-outlined" (click)="openAddRate()"></button>
            </div>

            <div class="table-scroll" *ngIf="visibleStateRates.length">
                <table class="state-rate-table">
                    <tr>
                        <th>State / region</th>
                        <th>Rate</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                    <tr *ngFor="let row of visibleStateRates">
                        <td>{{ row.state }}</td>
                        <td>{{ row.rate | number:'1.2-2' }}%</td>
                        <td>
                            <span class="pill" [class.active]="row.active" [class.muted]="!row.active">{{
                                row.active ? "Active" : "Inactive"
                            }}</span>
                        </td>
                        <td class="actions-cell">
                            <button class="icon-btn" type="button" (click)="editRate(row)">Edit</button>
                            <button class="icon-btn danger" type="button" (click)="removeRate(row)">Remove</button>
                        </td>
                    </tr>
                </table>
            </div>

            <div class="flex" *ngIf="!visibleStateRates.length">
                <span class="control-description text-muted">{{ taxBasis === 'origin' ? 'No locations added yet.' : 'No states added yet.' }}</span>
            </div>
        </div>
    </ng-container>

    <div class="info-note">
        <i class="pi pi-info-circle"></i>
        <div>
            <p><strong>Please Note:</strong> Tax rates are applied at the state level. Tax regulations may change by jurisdiction, so periodic review with a tax professional is recommended.</p>
            <p>Greater rate precision at the city and county level is planned for an upcoming release.</p>
        </div>
    </div>

    </ng-container>
</div>

<p-dialog
    header="{{ editingRateId != null ? 'Edit state tax rate' : 'Add a state you collect tax in' }}"
    [(visible)]="showAddRateModal"
    [modal]="true"
    [style]="{ width: '420px' }"
    [closable]="true"
    (onHide)="closeAddRateModal()"
>
    <p class="desc" style="margin-top: 0">
        Only add a state if you're required to collect tax there. Adding it here turns on tax collection for every order shipping to
        that state.
    </p>

    <div class="field">
        <label>State</label>
        <p-dropdown
            [(ngModel)]="addRateModel.state"
            (ngModelChange)="onAddRateStateChange($event)"
            [options]="usStates"
            optionLabel="name"
            optionValue="name"
            placeholder="Select a state…"
            [filter]="true"
            filterBy="name"
            filterPlaceholder="Search states…"
            [style]="{ width: '100%' }"
            appendTo="body"
        ></p-dropdown>
        <small *ngIf="isDuplicateState" style="color: #d9491e; display: block; margin-top: 4px;">
            {{ addRateModel.state }} is already in this list. Edit the existing row instead of adding it again.
        </small>
    </div>

    <div class="field">
        <label>Tax rate (%)</label>
        <input pInputText type="number" step="0.01" min="0" placeholder="e.g. 6.00" [(ngModel)]="addRateModel.rate" (input)="onAddRateRateInput()" (blur)="addRateModel.rate = roundToTwoDecimals(addRateModel.rate)" style="width: 100%" />
    </div>

    <ng-template pTemplate="footer">
        <button pButton pRipple type="button" label="Cancel" class="p-button-outlined" (click)="closeAddRateModal()"></button>
        <button
            pButton
            pRipple
            type="button"
            [label]="editingRateId != null ? 'Save changes' : 'Add state'"
            [disabled]="!isAddRateValid"
            (click)="saveRate()"
        ></button>
    </ng-template>
</p-dialog>
